/* eslint-disable max-lines */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import Button from '@components/atoms/ui-kit/button.tsx';
import toast from 'react-hot-toast';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { Card } from '@components/atoms/ui-kit/card';
import { Text } from '@components/atoms/ui-kit/text';
import { Badge } from '@components/atoms/ui-kit/badge';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';
import { useGetUserPreferences } from '@utils/api/organizations/user-preferences/use-get-user-preferences.tsx';
import { useDeleteUserPreference } from '@utils/api/organizations/user-preferences/use-detelete-user-preference.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { useAddPreference } from '@utils/api/organizations/user-preferences/use-add-user-preference.tsx';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  DISABLE_NUMPAD_SHORTCUTS_KEY,
  KEYMAP_INPUT_KEY
} from '@utils/constants/app-constants.ts';
import { useUpsertUserPreference } from '@utils/api/organizations/user-preferences/use-upsert-user-preference.tsx';
import { KeyboardKeymapInput } from '@utils/types/user-preferences/user-preferences.ts';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import { Trash2, Keyboard, Command, Plus } from 'lucide-react';
import PageHeader from '@components/layout/page-header.tsx';

interface ParsedPreferenceValue {
  key?: string;
  value?: string;
}

const Keymap = () => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const formRef = useRef<HTMLFormElement | null>(null);
  // const editFormRef = useRef<HTMLFormElement | null>(null);
  const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set());
  const [, setKeyInput] = useState('');
  const [, setKeyValue] = useState('');
  const [openModal, setOpenModal] = useState(false);

  const { data: preferences } = useGetUserPreferences();
  // user_preferences returns every preference row (theme, language, …);
  // only keymap rows belong on this screen.
  const keymapPreferences =
    preferences?.result.filter(item => item.key === KEYMAP_INPUT_KEY) ?? [];
  const numpadDisabled =
    preferences?.result.find(item => item.key === DISABLE_NUMPAD_SHORTCUTS_KEY)
      ?.value === 'true';
  const { mutate: deleteTemplate, isLoading: deleteIsLoading } =
    useDeleteUserPreference();
  const { mutate: addPreference } = useAddPreference();
  const { mutate: upsertPreference } = useUpsertUserPreference();
  // const { mutate: editPreference } = useEditPreference();

  const schema = yup.object().shape({
    key: yup.string().required(t('key-required')),
    value: yup.string().required(t('value-required'))
  });

  const [isFocused, setIsFocused] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      key: '',
      value: ''
    }
  });

  useEffect(() => {
    if (!isFocused) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();

      setPressedKeys(prev => {
        const newKeys = new Set(prev);

        if (newKeys.size >= 3) {
          const firstKey = Array.from(newKeys)[0];
          newKeys.delete(firstKey);
        }

        newKeys.add(event.key);
        return newKeys;
      });
    };

    const handleKeyUp = () => {
      if (pressedKeys.size >= 2 && pressedKeys.size <= 3) {
        const combination = Array.from(pressedKeys).join(' + ');
        setKeyInput(combination);
        setValue('key', combination);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pressedKeys, isFocused, setValue]);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const handleSave = (data: KeyboardKeymapInput) => {
    toast('Saving custom key...', { icon: '📝' });

    addPreference(
      {
        key: KEYMAP_INPUT_KEY,
        value: JSON.stringify(data)
      },
      {
        onSuccess: () => {
          toast.success(t('custom-key-added'));
          setOpenModal(false);
          reset();
        },
        onError: (error: AxiosError<BaseError>) => {
          const fallbackMessage = t('failed-to-add-preference');
          const data = error?.response?.data;

          let messages: string[] = [];

          if (Array.isArray(data?.detail)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            messages = data.detail.map((err: any) => {
              const field = err.loc?.[1] ?? 'field';
              return `${field}: ${err.msg}`;
            });
          } else if (data?.message) {
            messages = [data.message];
          } else if (data?.error_description) {
            messages = [data.error_description];
          } else {
            messages = [fallbackMessage];
          }

          toast.error(messages.join('\n'));
        }
      }
    );

    reset(
      {
        key: '',
        value: ''
      },
      { keepErrors: false }
    );
  };

  const handleDeleteFromApi = (id: number) => {
    if (!id || deleteIsLoading) return;

    toast('Deleting custom key...', { icon: '🗑️' });

    deleteTemplate(id, {
      onSuccess: () => {
        toast.success(t('custom-key-deleted'));
      },
      onError: (error: AxiosError<BaseError>) => {
        const fallbackMessage = t(
          'an-error-occurred-while-deleting-preference'
        );
        const data = error?.response?.data;

        let messages: string[] = [];

        if (Array.isArray(data?.detail)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          messages = data.detail.map((err: any) => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (data?.message) {
          messages = [data.message];
        } else if (data?.error_description) {
          messages = [data.error_description];
        } else {
          messages = [fallbackMessage];
        }

        toast.error(messages.join('\n'));
      }
    });
  };

  function isValidJson(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  }

  return (
    <Card padding="sm" className="sm:p-6">
      <PageHeader
        title={t('keymap-settings')}
        caption={t('view-and-add-custom-key-for-transcript-editor-use')}
        className="border-b-0 mb-4"
        contentClassName="px-0 py-0"
      >
        <Button
          icon={<Plus width={16} />}
          label={t('add-keymap')}
          size="small"
          variant="secondary"
          onClick={() => setOpenModal(true)}
        />
      </PageHeader>
      <div className="flex items-center justify-between py-3 border-b border-hairline mb-4">
        <div>
          <Text as="p" size="sm" weight="medium">
            {t('disable-numpad-shortcuts')}
          </Text>
          <Text as="p" size="xs" tone="muted">
            {t('disable-numpad-shortcuts-description')}
          </Text>
        </div>
        <button
          role="switch"
          aria-checked={numpadDisabled}
          onClick={() =>
            upsertPreference({
              key: DISABLE_NUMPAD_SHORTCUTS_KEY,
              value: numpadDisabled ? 'false' : 'true'
            })
          }
          className={cn(
            'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none',
            numpadDisabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200',
              numpadDisabled ? 'translate-x-4' : 'translate-x-0'
            )}
          />
        </button>
      </div>
      {keymapPreferences.length > 0 ? (
        <div className="mt-4 max-h-[600px] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {keymapPreferences.map(preferenceItem => {
              let parsedValue: ParsedPreferenceValue = {
                key: '',
                value: ''
              };

              try {
                // Check if the value is a valid JSON string
                if (
                  preferenceItem?.value &&
                  isValidJson(preferenceItem.value)
                ) {
                  parsedValue = JSON.parse(
                    preferenceItem.value
                  ) as ParsedPreferenceValue;
                }
              } catch (error) {
                // Error parsing JSON value
              }

              return (
                <Card
                  key={preferenceItem.id}
                  interactive
                  padding="sm"
                  className="transition-all"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <Text
                        as="h4"
                        size="sm"
                        weight="semibold"
                        truncate="clamp2"
                        title={t('keymap-user-setting-transcript-editor')}
                      >
                        {t('keymap-user-setting-transcript-editor')}
                      </Text>
                      <Badge color="zinc" className="mt-1">
                        {t('custom')}
                      </Badge>
                    </div>
                  </div>

                  {/* Card Body - Keymap Info */}
                  <div className="space-y-1.5 mb-3 min-h-[40px]">
                    <div className="flex items-center gap-1.5">
                      <Keyboard
                        size={12}
                        style={{ color: getColour('inactiveText') }}
                      />
                      <Text
                        as="span"
                        size="xs"
                        tone="muted"
                        truncate
                        className="flex-1 font-mono"
                        title={parsedValue.key || 'N/A'}
                      >
                        {parsedValue.key || 'N/A'}
                      </Text>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Command
                        size={12}
                        style={{ color: getColour('inactiveText') }}
                      />
                      <Text
                        as="span"
                        size="xs"
                        tone="muted"
                        truncate
                        className="flex-1"
                        title={parsedValue.value || 'N/A'}
                      >
                        {parsedValue.value || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Card Footer - Actions */}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-hairline">
                    <button
                      onClick={() =>
                        handleDeleteFromApi(preferenceItem.id || 0)
                      }
                      className="inline-flex h-7 w-full items-center justify-center gap-1 rounded-2xl hover:opacity-80 transition-opacity text-xs bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300"
                      title="Delete keymap"
                    >
                      <Trash2 size={12} />
                      <span className="hidden sm:inline">{t('delete')}</span>
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ) : (
        <EmptyState
          icon={<Keyboard size={32} />}
          title={t('no-keymaps-found')}
          subtitle={t('click-add-keymap-to-create')}
        />
      )}
      {openModal && (
        <Modal
          isOpen={openModal}
          onClose={() => {
            setOpenModal(false);
          }}
          size="small"
          title={t('create-new-shortcut')}
          caption={t('create-new-shortcut-caption')}
          footerActions={[
            <Button
              onClick={() => {
                setOpenModal(false);
              }}
              label={t('cancel')}
              size={'small'}
              variant={'smoke'}
            />,
            <Button
              key="create"
              type={'submit'}
              onClick={() => formRef.current?.requestSubmit()}
              label={t('create')}
              size={'small'}
              variant={'primary'}
            />
          ]}
        >
          <form
            id="keymap-form"
            ref={formRef}
            onSubmit={handleSubmit(handleSave)}
            className="space-y-2 mx-4"
          >
            <Controller
              name="key"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="key"
                  label={t('key')}
                  type="text"
                  value={field.value}
                  onChange={e => {
                    const value = e.target.value;
                    field.onChange(value);
                    setKeyInput(value);
                  }}
                  placeholder={t('ex."Ctrl + f" or "Ctrl + Shift + f"')}
                  className={cn('block min-w-56')}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  error={errors.key && errors.key.message}
                />
              )}
            />

            <div className="flex justify-end">
              {pressedKeys.size > 0 && (
                <Text
                  as="p"
                  size="xs"
                  tone="brand"
                  className="pt-0 px-1 rounded-2xl block w-full cursor-pointer hover:opacity-80"
                  onClick={() => {
                    setPressedKeys(new Set());
                    setKeyInput('');
                    setValue('key', '');
                  }}
                >
                  {t('clear-combination')}
                </Text>
              )}
            </div>

            <Controller
              name="value"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="value"
                  label={t('value')}
                  type="text"
                  value={field.value}
                  onChange={e => {
                    field.onChange(e.target.value);
                    setKeyValue(e.target.value); // Optional: Update local state
                  }}
                  placeholder={t('enter-value-for-shortcut')}
                  className={cn('block min-w-56')}
                  error={errors.value && errors.value.message}
                />
              )}
            />
          </form>
        </Modal>
      )}
    </Card>
  );
};

export default Keymap;
